import React from "react";
import { MapPin, Phone, Mail, Facebook, Instagram, Shield, FileText } from "lucide-react";
import { useConfig } from "../../contexts/ConfigContext";

function Footer() {
  const { config } = useConfig();
  const whatsappUrl = config.whatsapp ? `https://wa.me/${String(config.whatsapp).replace(/\D/g, "")}` : "";
  const name = config.nombreComercial || "Imporisel";
  const address = config.direccion || "Carrera 68 D # 10 A 25";
  const city = config.ciudad || "Bogotá, Colombia";
  const email = config.correoPublico || "Imporisel@gmail.com";
  const phone = config.telefono || "+57 3103379628";
  return (
    <footer className="bg-black text-gray-300 pt-16 pb-8 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

          {/* Columna 1: Marca y Sobre Nosotros */}
          <div className="space-y-4">
            <h3 className="text-3xl font-black text-white italic tracking-widest uppercase">
              {name}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              {config.descripcionPublica || config.eslogan || "Tu aliado estratégico en importaciones y soluciones logísticas. Calidad y cumplimiento en cada entrega."}
            </p>
          </div>

          {/* Columna 2: Contacto */}
          <div>
            <h4 className="text-white font-semibold mb-6 flex items-center gap-2">
              <span className="w-8 h-1 bg-yellow-400 rounded-full"></span>
              Contacto
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-400">
                  {address} <br /> {city}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <a href={`tel:${phone}`} className="text-sm text-gray-400 hover:text-white">{phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <a href={`mailto:${email}`} className="text-sm text-gray-400 hover:text-white transition-colors">
                  {email}
                </a>
              </li>
            </ul>
          </div>

          {/* Columna 3: Accesos Legales */}
          <div>
            <h4 className="text-white font-semibold mb-6 flex items-center gap-2">
              <span className="w-8 h-1 bg-yellow-400 rounded-full"></span>
              Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="/politica-privacidad"
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-yellow-400 transition-colors group"
                >
                  <Shield className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  Política de Privacidad
                </a>
              </li>
              <li>
                <a
                  href="/terminos-condiciones"
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-yellow-400 transition-colors group"
                >
                  <FileText className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  Términos y Condiciones
                </a>
              </li>
            </ul>
          </div>

          {/* Columna 4: Redes Sociales */}
          <div>
            <h4 className="text-white font-semibold mb-6 flex items-center gap-2">
              <span className="w-8 h-1 bg-yellow-400 rounded-full"></span>
              Síguenos
            </h4>
            <p className="text-sm text-gray-400 mb-4">Mantente al día con nuestras novedades.</p>
            <div className="flex gap-4">
              <a
                  href={config.facebook || "https://www.facebook.com/share/1B52MUxc52/?mibextid=wwXIfr"}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="text-yellow-400 hover:text-yellow-600 transition-all duration-300 transform hover:-translate-y-1"
              >
                <Facebook className="w-8 h-8" />
              </a>
              <a
                href={config.tiktok || "https://www.tiktok.com/@imporiselsas?_t=ZS-8wqoBIEinsw&_r=1"}
                target="_blank"
                rel="noreferrer"
                aria-label="TikTok"
                className="text-yellow-400 hover:text-yellow-600 transition-all duration-300 transform hover:-translate-y-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 50 50"
                  className="w-8 h-8 fill-current"
                >
                  <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.256,0.038-0.524,0.058-0.797,0.058c-2.122,0-3.995-0.835-5.408-2.181v9.853c0,4.642-3.791,8.411-8.529,8.411 c-4.717,0-8.553-3.752-8.553-8.384c0-4.606,3.812-8.358,8.502-8.358V26.2c-2.31,0.223-4.103,2.152-4.103,4.522 c0,2.5,2.055,4.532,4.582,4.532c2.518,0,4.561-2.022,4.561-4.512v-6c0-0.799,0.32-1.528,0.834-2.083 C28.169,22.062,28.895,21.725,29.7,21.725c0.697,0,1.344,0.254,1.847,0.672c1.948,1.603,4.406,2.6,7.108,2.7v-2.148 C38.006,22.95,37.607,22.95,37.006,22.323z M29.807,17.766c0.887-1.134,2.23-2.138,4.148-2.585v-2.617c-0.215,0.024-0.41,0.046-0.584,0.076 c-2.618,0.443-4.793,2.417-5.592,4.981C28.274,17.846,28.847,17.971,29.807,17.766z"></path>
                </svg>
              </a>
              <a
                href={config.instagram || "https://www.instagram.com/imporiselsas/"}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="text-yellow-400 hover:text-yellow-600 transition-all duration-300 transform hover:-translate-y-1"
              >
                <Instagram className="w-8 h-8" />
              </a>
              {config.mostrarWhatsapp && whatsappUrl && (
                <a href={whatsappUrl} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="text-yellow-400 hover:text-yellow-600 text-xs font-bold self-center">WhatsApp</a>
              )}
            </div>
          </div>

        </div>

        {/* Barra Inferior */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 gap-4">
          <p>&copy; {new Date().getFullYear()} {name} S.A.S. Todos los derechos reservados.</p>
          <div className="flex gap-6">
            <a href="/politica-privacidad" className="hover:text-yellow-400 transition-colors">Privacidad</a>
            <a href="/terminos-condiciones" className="hover:text-yellow-400 transition-colors">Términos</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
